/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { ScreenType } from 'client-code/previews';
import { AiringOffer } from 'client-code/airing';
import { Channel } from '../entities/Channel';
import { Log, QoEAppLogger, VuLogger } from '../logging';
import { PlaybackRestriction } from './PlaybackRestriction';
import { MetadataState, PlaybackRestrictionType, PlayerViewMode, VideoPlayerStates } from './enums';
import { VideoPlayerModelImpl } from './impls';
import { IMetadata, IMetadataManager, IPlayerStatus, IVideoPlayer } from './interfaces';
import { ServicesClientImpl } from '../impls/ServicesClientImpl';
import { SocuDataModelImpl } from '../socu/impls';
import { PlaybackContentType } from '../socu/enums/PlaybackContentType';
import { IPlayable } from '../interfaces';
import {
    APPLICATION_FEATURE_AREA,
    MILLISECONDS_IN_A_SECOND,
    LAST_VIEWED_POSITION_INTERVAL_TIME,
    SPEED,
    IN_HOME_LOCATION_PLAYBACK_GRACE_PERIOD,
} from '../../config';
import { NetworkManager } from '../network/NetworkManager';
import { DeviceModel } from '../device';
// TODO: need to eliminate the usage of Constants.
import { Constants } from '../constants';
import { GracePeriodUtils } from '../utils';
import { MetadataManager } from './MetadataManager';
import { TOfferPreviewCustomData } from '../types';
import { AppUtils } from '../AppUtils';
import { VideoPlayerModel } from './interfaces/IVideoPlayerModel';

export class VideoManager {
    private static TAG: string = 'VideoManager';

    private static CssClasses = {
        HIDDEN: 'hidden',
        MAXIMIZE: 'maximize',
        MINIMIZE: 'minimize',
    };

    private static mCallbackCounter: number = 0;

    private static mInHomeLocationChangeCallbacks: Map<number, Function> = new Map<
        number,
        Function
    >();

    private static mPlaybackErrorCallbacks: Map<number, Function> = new Map<number, Function>();

    private static mPlaybackRestrictionCallbacks: Map<number, Function> = new Map<
        number,
        Function
    >();

    private static mPlayerViewModeChangeCallbacks: Map<number, Function> = new Map<
        number,
        Function
    >();

    private static mProgramBoundaryCallbacks: Map<number, Function> = new Map<number, Function>();

    private static mReadyCallbacks: Map<number, Function> = new Map<number, Function>();

    private static mStreamRestrictionChangeCallbacks: Map<number, Function> = new Map<
        number,
        Function
    >();

    private static mVideoManager: VideoManager | null = null;

    private static mVideoPlayerStateChangeCallbacks: Map<number, Function> = new Map<
        number,
        Function
    >();

    private mGracePeriodTimerId: any = null;

    private mIsForcedPaused: boolean;

    // TODO can be removed
    private mIsMetadataLoaded: boolean;

    private mIsPlayerLoaded: boolean;

    private mIsPlaying: boolean;

    private mIsReady: boolean;

    private mLVPIntervalId: any | null;

    private mMetadataManager: IMetadataManager;

    private mPlayable: IPlayable | null;

    private mPlaybackRestrictionType: PlaybackRestrictionType;

    private mPlayerError: any;

    private mPlayerViewMode: PlayerViewMode;

    private mProgramBoundaryTimeoutId: number | null;

    private mSeekRangeStartTimeInMs: number | undefined;

    private mUiContainerRef: HTMLDivElement | null;

    private mVideoEl: HTMLVideoElement | null;

    private mVideoPlayer: IVideoPlayer | null;

    private mVideoPlayerModel: VideoPlayerModel | null;

    private constructor() {
        this.mIsForcedPaused = false;
        this.mIsMetadataLoaded = false;
        this.mIsPlayerLoaded = false;
        this.mIsPlaying = false;
        this.mIsReady = false;
        this.mMetadataManager = new MetadataManager();
        this.mSeekRangeStartTimeInMs = 0;
        this.mPlayable = null;
        this.mPlayerError = null;
        // TODO: Later,it should be used from device settings when homescreen comes in place.
        this.mPlayerViewMode = PlayerViewMode.VideoWindow;
        this.mProgramBoundaryTimeoutId = null;
        this.mUiContainerRef = null;
        this.mVideoEl = null;
        this.mVideoPlayer = null;
        this.mVideoPlayerModel = null;
        this.mPlaybackRestrictionType = PlaybackRestrictionType.NoRestriction;
        this.checkInHomeRestrictionWithGracePeriod =
            this.checkInHomeRestrictionWithGracePeriod.bind(this);
        NetworkManager.subscribe(this.onNetworkRestrictionHandler.bind(this));
        ServicesClientImpl.inHomeLocationDataUpdateEvent.subscribe(
            this.checkInHomeRestrictionWithGracePeriod,
        );
        this.addMetadataStateChangeListener(this.onMetadataManagerStateChangeHandler.bind(this));
    }

    // eslint-disable-next-line class-methods-use-this
    private onInHomeLocationChangeHandler(data): void {
        VideoManager.mInHomeLocationChangeCallbacks.forEach((callback) => {
            try {
                callback(data);
            } catch (err) {
                Log.e(VideoManager.TAG, 'onInHomeLocationChangeHandler callback error', err);
            }
        });
    }

    // eslint-disable-next-line class-methods-use-this
    private onNetworkRestrictionHandler(): void {
        try {
            let state = PlaybackRestrictionType.NetworkConnected;
            if (NetworkManager.isNetworkDisconnected()) {
                state = PlaybackRestrictionType.NetworkDisconnected;
                this.mVideoPlayerModel?.selectSpeed();
                this.pause();
            }
            this.mPlaybackRestrictionType = state;
            VideoManager.mPlaybackRestrictionCallbacks.forEach((callback) => {
                callback(state);
            });
            if (!NetworkManager.isNetworkDisconnected()) {
                VideoManager.getInstance().restorePlayback();
            }
        } catch (error) {
            Log.e(VideoManager.TAG, 'onNetworkRestrictionHandler callback error', error);
        }
    }

    // eslint-disable-next-line class-methods-use-this
    private onPlaybackErrorHandler(error: any): void {
        this.mPlayerError = error;
        VideoManager.mPlaybackErrorCallbacks.forEach((callback) => {
            try {
                callback(error);
            } catch (err) {
                Log.e(VideoManager.TAG, 'onPlaybackErrorHandler callback error', err);
            }
        });
    }

    // eslint-disable-next-line class-methods-use-this
    private onPlaybackRestrictionHandler(type: PlaybackRestrictionType): void {
        VideoManager.mPlaybackRestrictionCallbacks.forEach((callback) => {
            try {
                this.mPlaybackRestrictionType = type;
                callback(type);
            } catch (err) {
                Log.e(VideoManager.TAG, 'onPlaybackRestrictionHandler callback error', err);
            }
        });
    }

    // eslint-disable-next-line class-methods-use-this
    private onPlayerViewModeChangeHandler(playerViewMode: PlayerViewMode): void {
        this.mPlayerViewMode = playerViewMode;
        VideoManager.mPlayerViewModeChangeCallbacks.forEach((callback) => {
            try {
                callback(playerViewMode);
            } catch (err) {
                Log.e(VideoManager.TAG, 'onPlayerViewModeChangeHandler  error', err);
            }
        });
    }

    // eslint-disable-next-line class-methods-use-this
    private onReadyHandler(): void {
        if (this.mIsReady) return;
        this.mIsReady = true;
        if (!this.mIsPlaying && this.mPlayable) {
            this.startPlayback(this.mPlayable);
        }
        VideoManager.mReadyCallbacks.forEach((callback) => {
            try {
                callback();
            } catch (err) {
                Log.e(VideoManager.TAG, 'onReady callback error', err);
            }
        });
    }

    // eslint-disable-next-line class-methods-use-this
    private onStreamRestrictionChangeHandler(): void {
        VideoManager.mStreamRestrictionChangeCallbacks.forEach((callback) => {
            try {
                callback();
            } catch (err) {
                Log.e(VideoManager.TAG, 'onStreamRestrictionChangeHandler callback error', err);
            }
        });
    }

    // eslint-disable-next-line class-methods-use-this
    private onVideoPlayerStateChangeHandler(status: IPlayerStatus): void {
        Log.d(VideoManager.TAG, 'The video player state changed to', status);
        switch (status.state) {
            case VideoPlayerStates.END_OF_STREAM:
                this.onVideoPlayerEndOfStream();
                break;
            case VideoPlayerStates.FATAL_ERROR:
                this.mPlayerError = status.error;
                break;
            case VideoPlayerStates.FIRST_FRAME:
                this.onVideoPlayerFirstFrame();
                break;
            case VideoPlayerStates.INITIATION:
                this.mPlayerError = null;
                break;
            case VideoPlayerStates.LOADED:
                this.onPlayerLoaded();
                break;
            case VideoPlayerStates.PAUSE:
                clearInterval(this.mLVPIntervalId);
                break;
            case VideoPlayerStates.PLAYING:
                if (SocuDataModelImpl.getInstance().isSocu()) {
                    this.setLastViewedPositionInterval();
                } else
                    DeviceModel.getAppSettings().setLastTunedChannelNumber(
                        this.mPlayable?.channel.channelNumber!,
                    );
                break;
            case VideoPlayerStates.READY:
                this.onPlayerReady();
                break;
            case VideoPlayerStates.UNLOADED:
                this.mIsPlaying = false;
                this.mIsPlayerLoaded = false;
                clearInterval(this.mLVPIntervalId);
                break;
            default:
        }
        VideoManager.mVideoPlayerStateChangeCallbacks.forEach((callback) => {
            try {
                callback(status);
            } catch (err) {
                Log.e(VideoManager.TAG, 'onVideoPlayerStateChange callback error', err);
            }
        });
    }

    private onVideoPlayerEndOfStream(): void {
        const actionDetails = SocuDataModelImpl.getInstance().getActionDetails();
        const startTime = new Date(actionDetails.startTime).getTime() / MILLISECONDS_IN_A_SECOND;
        const endOfSoCuContent = startTime + (actionDetails.kernel.duration ?? 0);
        const currentTime =
            (this.mVideoPlayerModel?.getCurrentTimeInMs() ?? new Date().getTime()) /
            MILLISECONDS_IN_A_SECOND;

        const isEndOfProgram = currentTime > endOfSoCuContent;

        // TODO this need to be removed once this is fixed in player
        if (
            SocuDataModelImpl.getInstance().isStartOver() &&
            !this.mVideoPlayer?.isLive() &&
            !isEndOfProgram &&
            this.mSeekRangeStartTimeInMs
        ) {
            const currentTimeInMs = this.getVideoPlayerModel().getCurrentTimeInMs();
            const lastViewedPosition = Math.round(
                (currentTimeInMs - this.mSeekRangeStartTimeInMs) / MILLISECONDS_IN_A_SECOND,
            );
            this.mVideoPlayerModel?.restartPlaybackWithPosition(lastViewedPosition);
        } else {
            this.switchToLiveTv();
        }
    }

    private onVideoPlayerFirstFrame(): void {
        if (this.mPlayable?.playbackContentType === PlaybackContentType.LIVE) {
            this.mVideoPlayer?.overrideAvailabilityWindowForLinear();
        }
        this.mSeekRangeStartTimeInMs = this.mVideoPlayer?.getSeekRangeStartTimeInMs();
    }

    private onMetadataManagerStateChangeHandler(state: MetadataState): void {
        Log.d(VideoManager.TAG, 'The metadata state changed to', state);
        const isLiveMetadata =
            state === MetadataState.AIRING_READY ||
            state === MetadataState.PREVIEW_READY ||
            state === MetadataState.PREVIEW_ERROR;
        if (isLiveMetadata) {
            this.scheduleProgramBoundary();
        }
        switch (state) {
            case MetadataState.AIRING_READY:
                VuLogger.updateLiveTvPayload(
                    this.mPlayable?.channel!,
                    this.getMetadata().airingOffers!,
                );
                VuLogger.logWatchStartedEvent();
                break;
            case MetadataState.AIRING_ERROR:
                this.stop();
                break;
            case MetadataState.PREVIEW_READY:
            case MetadataState.PREVIEW_ERROR:
                this.onMetadataLoaded();
                if (this.mIsPlayerLoaded) this.logStreamingEvents();
                break;
            case MetadataState.VOD_PREVIEW_READY:
                this.mIsMetadataLoaded = true;
                this.onVODPreviewLoaded();
                if (this.mIsPlayerLoaded) this.logStreamingEvents();
                break;
            case MetadataState.VOD_PREVIEW_ERROR:
                this.onVODPreviewLoaded();
                break;
            default:
        }
    }

    private async onMetadataLoaded(): Promise<void> {
        this.mIsMetadataLoaded = true;
        const playbackRestrictionType = new PlaybackRestriction().checkAllRestriction(
            this.mPlayable!,
            this.getMetadata().airingOffers!,
            this.getMetadata().offerPreview!,
        );
        this.onPlaybackRestrictionHandler(playbackRestrictionType);
        switch (playbackRestrictionType) {
            case PlaybackRestrictionType.Adult:
            case PlaybackRestrictionType.Unsubscribed:
            case PlaybackRestrictionType.Blacklisted:
            case PlaybackRestrictionType.OutOfHome:
            case PlaybackRestrictionType.OutOfRegion:
                await this.stop();
                break;
            case PlaybackRestrictionType.NoRestriction:
                this.onNoRestriction();
                break;
            default:
        }
    }

    private onNoRestriction(): void {
        if (!this.mIsForcedPaused && this.mIsPlayerLoaded) {
            this.unPause();
        }
        this.checkInHomeRestrictionWithGracePeriod();
    }

    private onVODPreviewLoaded(): void {
        const metadata = this.getMetadata();
        const { offerPreview } = metadata;

        // Check if offerPreview is invalid
        if (!offerPreview?.contentPreview) {
            this.stop();
            this.onPlaybackRestrictionHandler(PlaybackRestrictionType.Adult);
            return;
        }

        const playbackRestrictionType = new PlaybackRestriction().checkAllRestriction(
            this.mPlayable!,
            metadata.airingOffers!,
            offerPreview,
        );
        this.onPlaybackRestrictionHandler(playbackRestrictionType);

        // Stop playback if there are restrictions
        if (playbackRestrictionType !== PlaybackRestrictionType.NoRestriction) {
            this.stop();
            return;
        }

        // Resume playback if not forced paused
        if (!this.mIsForcedPaused) this.unPause();
    }

    private checkInHomeRestrictionWithGracePeriod(): boolean {
        let isInHomeLocationRestriction = false;
        const service = new ServicesClientImpl();
        const inHomeLocationData = service.getInHomeLocation();
        const consumedPeriod = GracePeriodUtils.getInstance().getConsumedGracePeriod();
        switch (inHomeLocationData.status) {
            case Constants.IN_HOME_LOCATION_STATUS.ERROR:
                if (this.isPlaying()) this.stop();
                this.onPlaybackRestrictionHandler(PlaybackRestrictionType.InHomeLocationError);
                isInHomeLocationRestriction = true;
                break;
            case Constants.IN_HOME_LOCATION_STATUS.SUCCESS:
                if (consumedPeriod) GracePeriodUtils.getInstance().setConsumedGracePeriod(0);
                clearTimeout(this.mGracePeriodTimerId);
                this.mGracePeriodTimerId = null;
                isInHomeLocationRestriction = false;
                break;
            case Constants.IN_HOME_LOCATION_STATUS.UNREACHABLE:
                if (consumedPeriod < IN_HOME_LOCATION_PLAYBACK_GRACE_PERIOD)
                    this.startGracePeriod();
                else {
                    if (this.isPlaying()) this.stop();
                    this.onPlaybackRestrictionHandler(
                        PlaybackRestrictionType.InHomeLocationUnreachable,
                    );
                    isInHomeLocationRestriction = true;
                }
                break;
            default:
        }
        return isInHomeLocationRestriction;
    }

    // eslint-disable-next-line class-methods-use-this
    private getEndTimeOfCurrentProgram(
        airingOffers?: AiringOffer[],
        offerPreview?: TOfferPreviewCustomData,
    ) {
        const duration = airingOffers?.[0]?.duration || offerPreview?.contentPreview?.duration || 0;
        const startTime =
            airingOffers?.[0]?.startTime || offerPreview?.contentPreview?.startTime || '';

        if (duration !== 0 && startTime !== '') {
            return AppUtils.addSecondsToDateTime(new Date(startTime), duration);
        }

        if (offerPreview?.contentPreview?.endTime) {
            return new Date(offerPreview.contentPreview.endTime);
        }

        return new Date();
    }

    private startGracePeriod() {
        if (!this.mGracePeriodTimerId) {
            const startTime = Date.now();
            this.mGracePeriodTimerId = setTimeout(() => {
                GracePeriodUtils.getInstance().setConsumedGracePeriod(Date.now() - startTime);
                this.mGracePeriodTimerId = null;
                this.checkInHomeRestrictionWithGracePeriod();
            }, IN_HOME_LOCATION_PLAYBACK_GRACE_PERIOD);
        }
    }

    private onPlayerLoaded(): void {
        this.mIsPlayerLoaded = true;
        this.mVideoPlayerModel?.filterAndUpdateCurrentAudioSelectedTrack();
        if (SocuDataModelImpl.getInstance().isSocu()) {
            this.unPause();
        } else if (this.mIsMetadataLoaded) {
            this.unPause();
        }
    }

    private onPlayerReady(): void {
        if (this.mIsMetadataLoaded) {
            this.logStreamingEvents();
        }
    }

    private logStreamingEvents(): void {
        QoEAppLogger.logStreamingStartEvent();
        QoEAppLogger.logSessionCreatedEvent();
        QoEAppLogger.logVideoPlayerOpenStreamEvent();
    }

    private setLastViewedPositionInterval(): void {
        this.mLVPIntervalId = setInterval(() => {
            if (SocuDataModelImpl.getInstance().isSocu()) {
                this.updateLastViewedPosition();
            }
        }, LAST_VIEWED_POSITION_INTERVAL_TIME);
    }

    private play(playable: IPlayable) {
        if (this.mPlayerError) this.mPlayerError = null;
        this.init();
        let playbackRestrictionType: PlaybackRestrictionType;
        if (this.isMetadataUpdateRequired()) {
            this.mMetadataManager.prepareMetadataForPlayable(playable);
            playbackRestrictionType = new PlaybackRestriction().checkCanPlayStartRestriction(
                playable,
            );
        } else {
            this.mIsMetadataLoaded = true;
            playbackRestrictionType = new PlaybackRestriction().checkAllRestriction(
                this.mPlayable!,
                this.getMetadata().airingOffers!,
                this.getMetadata().offerPreview!,
            );
            QoEAppLogger.logWatchContentStart(playable.channel, this.getMetadata().airingOffers);
        }
        this.onPlaybackRestrictionHandler(playbackRestrictionType);
        if (
            playbackRestrictionType === PlaybackRestrictionType.NoRestriction &&
            !this.checkInHomeRestrictionWithGracePeriod()
        ) {
            this.tuneToChannel(playable.channel);
        } else {
            this.clean();
            Log.i(VideoManager.TAG, 'Banned or Same channel recieved ');
        }
    }

    private tuneToChannel(channel: Channel): boolean {
        Log.d(VideoManager.TAG, 'tuneToChannel', channel);
        if (!(channel && channel.channelNumber)) {
            Log.d(VideoManager.TAG, 'tuneToChannel', 'invalid playable', channel);
            return false;
        }
        Log.d(VideoManager.TAG, 'tuneToChannel', 'a new playble received.');
        this.mVideoPlayerModel?.playUri(channel);
        this.mIsPlaying = true;
        return true;
    }

    private clearProgramBoundaryTimeout() {
        if (this.mProgramBoundaryTimeoutId) {
            clearTimeout(this.mProgramBoundaryTimeoutId);
            this.mProgramBoundaryTimeoutId = null;
        }
    }

    // eslint-disable-next-line class-methods-use-this
    private onProgramBoundaryHandler(programEndTime: number) {
        if (!this.mVideoPlayerModel) return;
        const updatedProgramCurrentTime = this.mVideoPlayerModel!.getCurrentTimeInMs();
        if (updatedProgramCurrentTime < programEndTime) {
            this.scheduleProgramBoundary();
        } else {
            VideoManager.mProgramBoundaryCallbacks.forEach((callback) => {
                try {
                    callback();
                } catch (err) {
                    Log.e(VideoManager.TAG, 'onProgramBoundaryHandler callback error', err);
                }
            });
        }
    }

    public scheduleProgramBoundary() {
        if (!this.mVideoPlayerModel) {
            return;
        }

        this.clearProgramBoundaryTimeout();
        const metadata = this.mMetadataManager.getMetadata();
        const { airingOffers, offerPreview } = metadata;

        if (!airingOffers || airingOffers.length === 0) {
            return;
        }

        const programCurrentTime = this.mVideoPlayerModel.getCurrentTimeInMs();
        const programEndTime = this.getEndTimeOfCurrentProgram(
            airingOffers,
            offerPreview,
        ).getTime();
        const programBoundaryTriggerTime = programEndTime - programCurrentTime;
        const onProgramBoundaryHandler = this.onProgramBoundaryHandler.bind(this);
        this.mProgramBoundaryTimeoutId = setTimeout(
            onProgramBoundaryHandler,
            programBoundaryTriggerTime,
            programEndTime,
        );
    }

    // eslint-disable-next-line class-methods-use-this
    public addInHomeLocationChangeListener(callback: Function): number {
        VideoManager.mCallbackCounter += 1;
        VideoManager.mInHomeLocationChangeCallbacks.set(VideoManager.mCallbackCounter, callback);
        return VideoManager.mCallbackCounter;
    }

    public addMetadataStateChangeListener(callback: Function): number {
        return this.mMetadataManager.addMetadataStateChangeListener(callback);
    }

    // eslint-disable-next-line class-methods-use-this
    public addPlaybackErrorListener(callback: Function): number {
        VideoManager.mCallbackCounter += 1;
        VideoManager.mPlaybackErrorCallbacks.set(VideoManager.mCallbackCounter, callback);
        return VideoManager.mCallbackCounter;
    }

    // eslint-disable-next-line class-methods-use-this
    public addPlaybackRestrictionListener(callback: Function): number {
        VideoManager.mCallbackCounter += 1;
        VideoManager.mPlaybackRestrictionCallbacks.set(VideoManager.mCallbackCounter, callback);
        return VideoManager.mCallbackCounter;
    }

    // eslint-disable-next-line class-methods-use-this
    public addPlayerViewModeChangeListener(callback: Function): number {
        VideoManager.mCallbackCounter += 1;
        VideoManager.mPlayerViewModeChangeCallbacks.set(VideoManager.mCallbackCounter, callback);
        return VideoManager.mCallbackCounter;
    }

    // eslint-disable-next-line class-methods-use-this
    public addReadyListener(callback: Function): number {
        VideoManager.mCallbackCounter += 1;
        VideoManager.mReadyCallbacks.set(VideoManager.mCallbackCounter, callback);
        return VideoManager.mCallbackCounter;
    }

    // eslint-disable-next-line class-methods-use-this
    public addStreamRestrictionChangeListener(callback: Function): number {
        VideoManager.mCallbackCounter += 1;
        VideoManager.mStreamRestrictionChangeCallbacks.set(VideoManager.mCallbackCounter, callback);
        return VideoManager.mCallbackCounter;
    }

    // eslint-disable-next-line class-methods-use-this
    public addVideoPlayerStateChangeListener(callback: Function): number {
        VideoManager.mCallbackCounter += 1;
        VideoManager.mVideoPlayerStateChangeCallbacks.set(VideoManager.mCallbackCounter, callback);
        return VideoManager.mCallbackCounter;
    }

    // eslint-disable-next-line class-methods-use-this
    public addProgramBoundaryListener(callback: Function): number {
        VideoManager.mCallbackCounter += 1;
        VideoManager.mProgramBoundaryCallbacks.set(VideoManager.mCallbackCounter, callback);
        return VideoManager.mCallbackCounter;
    }

    public clean(): void {
        this.stop();
    }

    public configurePlayer(videoEl: HTMLVideoElement, uiContainerRef: HTMLDivElement) {
        this.mVideoEl = videoEl;
        this.mUiContainerRef = uiContainerRef;
        const playerConfig = {
            playerEl: videoEl,
            playerRootEl: uiContainerRef,
        };
        this.mVideoPlayerModel = VideoPlayerModelImpl.getInstance();
        this.mVideoPlayerModel.configure(playerConfig);
        this.init();
    }

    public getIsPlayerLoaded(): boolean {
        return this.mIsPlayerLoaded;
    }

    public getMetadata(): IMetadata {
        return this.mMetadataManager.getMetadata();
    }

    public getMetadataState(): MetadataState {
        return this.mMetadataManager.getMetadataState();
    }

    public getPlayable(): IPlayable | null {
        return this.mPlayable;
    }

    public getPlaybackRestrictionType(): PlaybackRestrictionType {
        return this.mPlaybackRestrictionType;
    }

    public getPlayerError(): any {
        return this.mPlayerError;
    }

    public getPlayerViewMode(): string {
        return this.mPlayerViewMode;
    }

    public getVideoPlayerModel(): VideoPlayerModel {
        if (this.mVideoPlayerModel === null)
            throw new Error('The VideoPlayerModel has not been initialized.');
        return this.mVideoPlayerModel;
    }

    public hasPlayerError(): boolean {
        return !!this.mPlayerError;
    }

    public init(): void {
        if (this.mVideoPlayerModel) {
            this.mVideoPlayerModel.addPlaybackErrorListener(this.onPlaybackErrorHandler.bind(this));
            this.mVideoPlayerModel.addStreamRestrictionChangeListener(
                this.onStreamRestrictionChangeHandler,
            );
            this.mVideoPlayer = this.mVideoPlayerModel.getVideoPlayer();
            this.mVideoPlayer.addVideoPlayerStateChangeListener(
                this.onVideoPlayerStateChangeHandler.bind(this),
            );
        }
        ServicesClientImpl.inHomeLocationDataUpdateEvent.subscribe(
            this.onInHomeLocationChangeHandler,
        );
    }

    public isChannelRestricted(): boolean {
        return this.mPlaybackRestrictionType !== PlaybackRestrictionType.NoRestriction;
    }

    public isMetadataUpdateRequired(): boolean {
        const { airingOffers, offerPreview } = this.getMetadata();

        // Check for the cases in LIVE playback
        if (this.mPlayable?.playbackContentType === PlaybackContentType.LIVE) {
            // If airingOffers is not available or empty, metadata update is required
            if (!airingOffers || !airingOffers.length) return true;

            // Check if the channel has changed
            if (airingOffers[0].stationId !== this.mPlayable?.channel.stationId) return true;

            // Check if current time is greater than end time of current program
            const currentTime =
                this.mVideoPlayerModel!.getCurrentTimeInMs() || new Date().getTime();
            const endTimeOfCurrentProgram =
                this.getEndTimeOfCurrentProgram(airingOffers, offerPreview).getTime() || 0;
            return currentTime > endTimeOfCurrentProgram;
        }

        // Check if playback content type is changed to SOCU
        return SocuDataModelImpl.getInstance().isSocu();
    }

    public isPlayerLoaded(): boolean {
        return this.mIsPlayerLoaded;
    }

    public isPlaying(): boolean {
        return this.mIsPlaying;
    }

    public maximize(): void {
        this.mUiContainerRef?.classList.remove(VideoManager.CssClasses.MINIMIZE);
        this.mUiContainerRef?.classList.add(VideoManager.CssClasses.MAXIMIZE);
        this.mVideoPlayer?.unMute();
        this.onPlayerViewModeChangeHandler(PlayerViewMode.FullScreen);
    }

    public minimize(): void {
        this.mUiContainerRef?.classList.remove(VideoManager.CssClasses.MAXIMIZE);
        this.mUiContainerRef?.classList.add(VideoManager.CssClasses.MINIMIZE);
        if (this.mVideoPlayer?.isTrickPlayEnabled())
            this.mVideoPlayer?.setSpeed(SPEED.SPEED_NORMAL);
        this.onPlayerViewModeChangeHandler(PlayerViewMode.VideoWindow);
    }

    public mount(): void {
        this.mUiContainerRef?.classList.remove(VideoManager.CssClasses.HIDDEN);
    }

    public pause(forced: boolean = false): void {
        const canPause =
            this.mVideoPlayerModel?.isPlaybackStarted() &&
            this.mVideoPlayerModel?.trickModeRestrictions()?.allowPause;
        if (canPause) {
            this.mIsForcedPaused = forced || this.mIsForcedPaused;
            this.getVideoPlayerModel().pause();
            this.mIsPlaying = false;
        }
    }

    public refreshMetadata(playerCurrentTime: number, screenType?: ScreenType) {
        this.mMetadataManager.refreshMetadata(this.mPlayable!, playerCurrentTime, screenType);
    }

    public prepareMetadataForPlayable(playable: IPlayable) {
        if (this.isMetadataUpdateRequired())
            this.mMetadataManager.prepareMetadataForPlayable(playable);
        else {
            QoEAppLogger.logWatchContentStart(playable.channel, this.getMetadata().airingOffers);
            this.onMetadataLoaded();
        }
    }

    public ready(): void {
        this.onReadyHandler();
    }

    // eslint-disable-next-line class-methods-use-this
    public removeInHomeLocationChangeListener(ref: number): void {
        VideoManager.mInHomeLocationChangeCallbacks.delete(ref);
    }

    public removeMetadataStateChangeListner(ref: number): void {
        this.mMetadataManager.removeMetadataStateChangeListener(ref);
    }

    // eslint-disable-next-line class-methods-use-this
    public removePlaybackErrorListener(ref: number): void {
        VideoManager.mPlaybackErrorCallbacks.delete(ref);
    }

    // eslint-disable-next-line class-methods-use-this
    public removePlaybackRestrictionListner(ref: number): void {
        VideoManager.mPlaybackRestrictionCallbacks.delete(ref);
    }

    // eslint-disable-next-line class-methods-use-this
    public removePlayerViewModeChangeListner(ref: number): void {
        VideoManager.mPlayerViewModeChangeCallbacks.delete(ref);
    }

    // eslint-disable-next-line class-methods-use-this
    public removeReadyListener(ref: number): void {
        VideoManager.mReadyCallbacks.delete(ref);
    }

    // eslint-disable-next-line class-methods-use-this
    public removeStreamRestrictionChangeListener(ref: number): void {
        VideoManager.mStreamRestrictionChangeCallbacks.delete(ref);
    }

    // eslint-disable-next-line class-methods-use-this
    public removeVideoPlayerStateChangeListener(ref: number): void {
        VideoManager.mVideoPlayerStateChangeCallbacks.delete(ref);
    }

    // eslint-disable-next-line class-methods-use-this
    public removeProgramBoundaryListener(ref: number): void {
        VideoManager.mProgramBoundaryCallbacks.delete(ref);
    }

    public async restorePlayback() {
        const isVideoWindowDiabled = !DeviceModel.getAppSettings().isVideoWindowEnabled();
        const isPlayingInFullScreen = this.mPlayerViewMode === PlayerViewMode.FullScreen;
        const playbackRestrictionType = new PlaybackRestriction().checkCanPlayStartRestriction(
            this.mPlayable!,
        );
        this.mPlayerError = null;
        if (this.mPlayable?.channel === null || (isVideoWindowDiabled && !isPlayingInFullScreen))
            return;
        this.onPlaybackRestrictionHandler(playbackRestrictionType);
        if (playbackRestrictionType === PlaybackRestrictionType.NoRestriction) {
            if (this.mPlayable) {
                this.mIsPlayerLoaded
                    ? this.prepareMetadataForPlayable(this.mPlayable)
                    : this.startPlayback(this.mPlayable);
            }
        } else {
            this.clean();
            Log.i(VideoManager.TAG, 'Banned or Same channel recieved ');
        }
    }

    public async startPlayback(playable: IPlayable) {
        SocuDataModelImpl.getInstance().setPlaybackContentType(playable.playbackContentType);
        if (!this.mIsReady) {
            this.mPlayable = playable;
            return;
        }
        switch (playable.playbackContentType) {
            case PlaybackContentType.LIVE:
                if (
                    (!NetworkManager.isNetworkDisconnected() &&
                        !this.mIsPlaying &&
                        !this.mIsForcedPaused) ||
                    this.mPlayable?.channel.channelNumber !== playable.channel?.channelNumber ||
                    this.mPlayable?.playbackContentType !== playable?.playbackContentType ||
                    this.hasPlayerError()
                ) {
                    if (this.mIsPlaying) {
                        await this.stop();
                    }
                    this.mPlayable = playable;
                    SocuDataModelImpl.getInstance().setPlaybackContentType(
                        this.mPlayable.playbackContentType,
                    );
                    this.play(playable);
                }
                break;
            case PlaybackContentType.STARTOVER_BEGINNING:
            case PlaybackContentType.STARTOVER_RESUME:
            case PlaybackContentType.CATCHUP_BEGINNING:
            case PlaybackContentType.CATCHUP_RESUME:
                if (this.mIsPlaying) {
                    await this.stop();
                }
                this.mPlayable = playable;
                SocuDataModelImpl.getInstance().setPlaybackContentType(
                    this.mPlayable.playbackContentType,
                );
                this.play(playable);
                break;
            default:
        }
    }

    public async stop(): Promise<void> {
        this.mIsPlaying = false;
        this.mIsMetadataLoaded = false;
        this.mIsForcedPaused = false;
        this.mIsPlayerLoaded = false;
        QoEAppLogger.logPlaybackStopped();
        await this.mVideoPlayerModel?.stopPlayback();
    }

    public setCurrentPlayPosition(): void {
        const oSocuDataModelImpl = SocuDataModelImpl.getInstance();

        switch (oSocuDataModelImpl.getPlaybackContentType()) {
            case PlaybackContentType.CATCHUP_RESUME:
                this.mVideoPlayerModel?.seekTo(
                    new Date(
                        oSocuDataModelImpl.getActionDetails().kernel.savedPosition || 0,
                    ).getTime(),
                );
                break;

            case PlaybackContentType.STARTOVER_BEGINNING:
                this.mVideoPlayerModel?.seekTo(
                    this.mVideoPlayerModel?.getSeekRangeStartTimeInMs() / MILLISECONDS_IN_A_SECOND -
                        this.mVideoPlayerModel?.getCurrentTimeInMs() / MILLISECONDS_IN_A_SECOND,
                );
                break;

            case PlaybackContentType.STARTOVER_RESUME:
                this.mVideoPlayerModel?.seekTo(
                    this.mVideoPlayerModel?.getSeekRangeStartTimeInMs() / MILLISECONDS_IN_A_SECOND +
                        (oSocuDataModelImpl.getActionDetails().kernel.savedPosition || 0) -
                        this.mVideoPlayerModel?.getCurrentTimeInMs() / MILLISECONDS_IN_A_SECOND,
                );
                break;

            case PlaybackContentType.LIVE:
                this.mVideoPlayerModel?.goToLive();
                break;

            default:
        }
    }

    public switchToLiveTv(): void {
        const channel = this.mPlayable?.channel;
        Log.d(VideoManager.TAG, 'switchToLiveTv', channel);
        this.updateLastViewedPosition();
        SocuDataModelImpl.getInstance().resetSocuData();
        this.clean();
        if (channel) this.startPlayback({ channel, playbackContentType: PlaybackContentType.LIVE });
    }

    public unmount(): void {
        this.clean();
        this.mUiContainerRef?.classList.add(VideoManager.CssClasses.HIDDEN);
    }

    /**
     * TODO: Consider renaming this function as it does not reflect any functionality
     * supported by the player.
     */
    public unPause(): void {
        this.getVideoPlayerModel().unPause();
        this.mIsPlaying = true;
        this.mIsForcedPaused = false;
    }

    public updateLastViewedPosition(screenType: string = APPLICATION_FEATURE_AREA.PLAYER): void {
        SocuDataModelImpl.getInstance().updateLastViewedPosition(
            this.getVideoPlayerModel().getCurrentTimeInMs(),
            this.getVideoPlayerModel().getSeekRangeStartTimeInMs(),
            screenType,
        );
    }

    public static getInstance(): VideoManager {
        if (VideoManager.mVideoManager === null) {
            VideoManager.mVideoManager = new VideoManager();
        }
        return VideoManager.mVideoManager;
    }
}
